// OrgChartToolbar.jsx
// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  AppBar,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// components
// Adjust this import path to wherever your dialog actually lives
// import ConfigurationHealthCheckDialog from './ConfigurationHealthCheckDialog';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

// relative
import OrgChartControls from './OrgChartControls';

const OrgChartToolbar = ({
  selectedEnergyGroup,
  searchValue,
  handleSearch,
  availableTypes,
  selectedTypes,
  onTypeToggle,
  zoomIn,
  zoomOut,
  onOpenFilterDialog,
  fitToScreen,
  expandAllNodes,
  collapseAllNodes,
  collapseOneLevel,
  expandOneLevel,
  isDirty,
  isSaving,
  onSaveChanges,
  onDiscardChanges,
  validationResult,
  isValidating,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-tree-tab');

  // Responsive breakpoint
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xl'));

  const hasNodes = selectedEnergyGroup?.energyTreeNodes?.length > 0;

  const validationStatus = validationResult?.Status;

  const getSaveButtonTooltip = () => {
    if (!isDirty) {
      return t('tree_actions.no_changes', {
        defaultValue: 'No changes to save',
      });
    }
    if (isSaving) {
      return t('tree_actions.saving', { defaultValue: 'Saving…' });
    }
    if (isValidating) {
      return t('tree_actions.validating', { defaultValue: 'Validating…' });
    }
    if (validationStatus === 'Error') {
      return t('tree_actions.save_with_errors');
    }
    if (validationStatus === 'Warning') {
      return t('tree_actions.save_with_warnings');
    }
    return t('tree_actions.save_changes', { defaultValue: 'Save changes' });
  };

  const getSaveButtonLabel = () => {
    if (!isDirty) {
      return t('tree_actions.no_changes');
    }
    if (isSaving) {
      return t('tree_actions.saving');
    }
    if (isValidating) {
      return t('tree_actions.validating');
    }
    if (validationStatus === 'Error') {
      return t('tree_actions.save_with_errors');
    }
    if (validationStatus === 'Warning') {
      return t('tree_actions.save_with_warnings');
    }
    return t('tree_actions.save_changes');
  };

  const isPortfolio = selectedEnergyGroup?.type
    ?.toLowerCase()
    .includes('portfolio');

  // Builds the small status overlay icon shown on the Save button, reflecting
  // the current validation state (error / warning / success) so the button
  // itself communicates whether it's safe to save without a separate badge.
  const getValidationBadgeConfig = () => {
    switch (validationStatus) {
      case 'Error':
      case 'Critical':
        return {
          icon: <ErrorRoundedIcon color="error" sx={{ fontSize: 14 }} />,
          boxStyle: {
            backgroundColor: 'white',
            height: '14px',
            width: '14px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        };
      case 'Warning':
        return {
          icon: <WarningRoundedIcon color="warning" sx={{ fontSize: 14 }} />,
          boxStyle: {
            backgroundColor: 'white',
            height: '10px',
            width: '4px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        };
      case 'Success':
        return {
          icon: (
            <CheckCircleRoundedIcon color="success" sx={{ fontSize: 14 }} />
          ),
          boxStyle: {
            backgroundColor: 'white',
            height: '14px',
            width: '14px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        };
      default:
        return null;
    }
  };

  const validationBadgeConfig = getValidationBadgeConfig();

  const saveButtonIcon = isValidating ? (
    <CircularProgress size={16} thickness={5} />
  ) : validationBadgeConfig ? (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <Box sx={validationBadgeConfig.boxStyle}>
          {validationBadgeConfig.icon}
        </Box>
      }
    >
      <SaveRoundedIcon />
    </Badge>
  ) : (
    <SaveRoundedIcon />
  );

  return (
    <AppBar
      className="non-printable"
      data-testid="orgchart-toolbar"
      position="static"
      elevation={0}
      sx={{
        zIndex: 10, // ensure it’s above the chart
        backgroundColor: 'transparent',
        color: 'text.primary',
        borderRadius: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          px: 1.5,
          minHeight: 'auto',
        }}
      >
        {hasNodes && (
          <TextField
            type="search"
            placeholder={t('tree_actions.search')}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            data-testid="orgchart-search-input"
            sx={{
              minWidth: '300px',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[0],
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }
            }}
          />
        )}

        {/* Type filter toggle buttons */}
        {hasNodes && availableTypes && availableTypes.length > 0 && (
          <Box
            data-testid="orgchart-type-filter-container"
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflowX: 'auto',
              maxWidth: isSmallScreen ? '300px' : 'none',
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.divider,
                borderRadius: '6px',
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {availableTypes.map((type) => {
                const isSelected = selectedTypes.includes(type);
                return (
                  <Tooltip
                    key={type}
                    title={t(`node_type.${type}`, { defaultValue: type })}
                    placement="bottom"
                  >
                    <IconButton
                      onClick={() => {
                        // Toggle the type in the selectedTypes array
                        const newSelectedTypes = isSelected
                          ? selectedTypes.filter((t) => t !== type)
                          : [...selectedTypes, type];
                        onTypeToggle(newSelectedTypes);
                      }}
                      data-testid={`orgchart-type-filter-${type}`}
                      className={
                        isSelected
                          ? 'orgchart-type-filter-button selected'
                          : 'orgchart-type-filter-button'
                      }
                      sx={{
                        borderRadius: '6px',
                        border: isSelected ? '2px solid ' : '1px solid ',
                        borderColor: isSelected
                          ? '#2196F3'
                          : theme.palette.divider,
                        backgroundColor: isSelected
                          ? 'rgba(33, 150, 243, 0.1)'
                          : theme.palette.mode === 'dark'
                            ? '#121212'
                            : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? 'rgba(33, 150, 243, 0.2)'
                            : theme.palette.mode === 'dark'
                              ? '#2a2a2a'
                              : '#f5f5f5',
                        },
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: getTypeColor(type),
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <i
                          style={{ color: 'white', fontSize: '12px' }}
                          className={`fa-solid ${getTypeIcon(type)}`}
                        />
                      </Box>
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Draft / Save controls */}
        {hasNodes && !selectedEnergyGroup.type.includes('Portfolio') && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 'auto',
            }}
          >
            {isDirty && (
              <Tooltip title={t('tree_actions.reset')} placement="bottom">
                <span>
                  <Button
                    variant="text"
                    onClick={onDiscardChanges}
                    disabled={!isDirty || isSaving}
                    startIcon={<UndoRoundedIcon />}
                    data-testid="orgchart-discard-button"
                  >
                    {t('tree_actions.reset')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title={getSaveButtonTooltip()} placement="bottom">
              {/* Wrap Button in span so Tooltip works when disabled */}
              {!isSmallScreen ? (
                <span>
                  <Button
                    variant="contained"
                    onClick={onSaveChanges}
                    disabled={!isDirty || isSaving}
                    startIcon={saveButtonIcon}
                    data-testid="orgchart-save-button"
                  >
                    {getSaveButtonLabel()}
                  </Button>
                </span>
              ) : (
                <IconButton
                  color="primary"
                  onClick={onSaveChanges}
                  disabled={!isDirty || isSaving}
                  data-testid="orgchart-save-icon"
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1.1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {saveButtonIcon}
                </IconButton>
              )}
            </Tooltip>
          </Box>
        )}

        {hasNodes && (
          <OrgChartControls
            isSmallScreen={isSmallScreen}
            isPortfolio={isPortfolio}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            onOpenFilterDialog={onOpenFilterDialog}
            fitToScreen={fitToScreen}
            expandAllNodes={expandAllNodes}
            collapseAllNodes={collapseAllNodes}
            collapseOneLevel={collapseOneLevel}
            expandOneLevel={expandOneLevel}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default OrgChartToolbar;
